import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Edit2, Trash2, Home, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnitsPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const currency = language === 'tr' ? '₺' : '$';
  const [units, setUnits] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const defaultForm = {
    name: '',
    building_id: '',
    status: 'vacant',
    monthly_rent: '',
    daily_rent: '',
    water_fee: '',
    internet_fee: '',
    electricity_type: 'prepaid_card_meter',
    floor: '',
    size_sqm: '',
    notes: ''
  };

  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    fetchUnits();
    fetchBuildings();
  }, []);

  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/units`, { withCredentials: true });
      setUnits(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/buildings`, { withCredentials: true });
      setBuildings(response.data);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        monthly_rent: formData.monthly_rent ? parseFloat(formData.monthly_rent) : null,
        daily_rent: formData.daily_rent ? parseFloat(formData.daily_rent) : null,
        water_fee: formData.water_fee ? parseFloat(formData.water_fee) : null,
        internet_fee: formData.internet_fee ? parseFloat(formData.internet_fee) : null,
        floor: formData.floor ? parseInt(formData.floor) : null,
        size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null
      };

      if (editingUnit) {
        await axios.put(`${API_URL}/api/units/${editingUnit.id}`, payload, { withCredentials: true });
        toast.success(t('saveSuccess'));
      } else {
        await axios.post(`${API_URL}/api/units`, payload, { withCredentials: true });
        toast.success(t('saveSuccess'));
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchUnits();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name || '',
      building_id: unit.building_id || '',
      status: unit.status || 'vacant',
      monthly_rent: unit.monthly_rent?.toString() || '',
      daily_rent: unit.daily_rent?.toString() || '',
      water_fee: unit.water_fee?.toString() || '',
      internet_fee: unit.internet_fee?.toString() || '',
      electricity_type: unit.electricity_type || 'prepaid_card_meter',
      floor: unit.floor?.toString() || '',
      size_sqm: unit.size_sqm?.toString() || '',
      notes: unit.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/units/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchUnits();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setEditingUnit(null);
    setFormData(defaultForm);
  };

  // Duplicate functionality
  const openDuplicateModal = (unit) => {
    setDuplicateSource(unit);
    setDuplicateCount(1);
    setIsDuplicateModalOpen(true);
  };

  const handleDuplicate = async () => {
    if (!duplicateSource || duplicateCount < 1) return;
    setDuplicateLoading(true);
    
    try {
      const basePayload = {
        building_id: duplicateSource.building_id,
        status: 'vacant',
        monthly_rent: duplicateSource.monthly_rent,
        daily_rent: duplicateSource.daily_rent,
        water_fee: duplicateSource.water_fee,
        internet_fee: duplicateSource.internet_fee,
        electricity_type: duplicateSource.electricity_type,
        floor: duplicateSource.floor,
        size_sqm: duplicateSource.size_sqm,
        notes: duplicateSource.notes
      };

      for (let i = 1; i <= duplicateCount; i++) {
        const payload = {
          ...basePayload,
          name: `${duplicateSource.name} - ${language === 'tr' ? 'Kopya' : 'Copy'} ${i}`
        };
        await axios.post(`${API_URL}/api/units`, payload, { withCredentials: true });
      }

      toast.success(`${duplicateCount} ${language === 'tr' ? 'daire kopyalandı' : 'apartment(s) duplicated'}`);
      setIsDuplicateModalOpen(false);
      setDuplicateSource(null);
      fetchUnits();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDuplicateLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      vacant: 'status-vacant',
      occupied: 'status-occupied',
      reserved: 'status-reserved',
      maintenance: 'status-maintenance',
      blocked: 'status-blocked'
    };
    return `px-2 py-1 text-xs font-bold uppercase border ${classes[status] || 'bg-gray-100'}`;
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || unit.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="units-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('units')}</h1>
          <p className="text-gray-500 text-sm mt-1">{units.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-unit-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addUnit')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="units-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-none" data-testid="status-filter">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="vacant">{t('vacant')}</SelectItem>
            <SelectItem value="occupied">{t('occupied')}</SelectItem>
            <SelectItem value="reserved">{t('reserved')}</SelectItem>
            <SelectItem value="maintenance">{t('inMaintenance')}</SelectItem>
            <SelectItem value="blocked">{language === 'tr' ? 'Engelli' : 'Blocked'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Units Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('unitName')}</th>
              <th>{t('status')}</th>
              <th>{t('monthlyRent')}</th>
              <th>{t('dailyRent')}</th>
              <th>{t('floor')}</th>
              <th>{t('sizeSqm')}</th>
              <th>{t('electricityType')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUnits.length > 0 ? (
              filteredUnits.map((unit) => (
                <tr key={unit.id} data-testid={`unit-row-${unit.id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-gray-400" />
                      {unit.name}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadge(unit.status)}>
                      {t(unit.status) || unit.status}
                    </span>
                  </td>
                  <td className="font-mono">{unit.monthly_rent ? `${currency}${unit.monthly_rent}` : '-'}</td>
                  <td className="font-mono">{unit.daily_rent ? `${currency}${unit.daily_rent}` : '-'}</td>
                  <td>{unit.floor || '-'}</td>
                  <td>{unit.size_sqm ? `${unit.size_sqm} m²` : '-'}</td>
                  <td className="text-xs">
                    {unit.electricity_type === 'prepaid_card_meter' ? t('prepaidMeter') : t('regularMeter')}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => openDuplicateModal(unit)} 
                        className="p-1 hover:bg-blue-50 text-blue-600"
                        title={t('duplicateUnit')}
                        data-testid={`duplicate-unit-${unit.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleEdit(unit)} 
                        className="p-1 hover:bg-gray-100"
                        data-testid={`edit-unit-${unit.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(unit.id)} 
                        className="p-1 hover:bg-red-100 text-red-600"
                        data-testid={`delete-unit-${unit.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingUnit ? t('editUnit') : t('addUnit')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('unitName')} *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                  data-testid="unit-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{language === 'tr' ? 'Bina' : 'Building'} *</label>
                <Select 
                  value={formData.building_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, building_id: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="unit-building-select">
                    <SelectValue placeholder={t('selectBuilding')} />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('electricityType')}</label>
                <Select 
                  value={formData.electricity_type} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, electricity_type: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid_card_meter">{t('prepaidMeter')}</SelectItem>
                    <SelectItem value="regular">{t('regularMeter')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('status')}</label>
                <Select 
                  value={formData.status} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacant">{t('vacant')}</SelectItem>
                    <SelectItem value="occupied">{t('occupied')}</SelectItem>
                    <SelectItem value="reserved">{t('reserved')}</SelectItem>
                    <SelectItem value="maintenance">{t('inMaintenance')}</SelectItem>
                    <SelectItem value="blocked">{language === 'tr' ? 'Engelli' : 'Blocked'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('monthlyRent')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('dailyRent')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.daily_rent}
                  onChange={(e) => setFormData(prev => ({ ...prev, daily_rent: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('waterFee')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.water_fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, water_fee: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('internetFee')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.internet_fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, internet_fee: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('floor')}</label>
                <Input
                  type="number"
                  value={formData.floor}
                  onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('sizeSqm')}</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.size_sqm}
                  onChange={(e) => setFormData(prev => ({ ...prev, size_sqm: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="3"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="unit-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Modal */}
      <Dialog open={isDuplicateModalOpen} onOpenChange={setIsDuplicateModalOpen}>
        <DialogContent className="max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {t('duplicateUnitTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {duplicateSource && (
              <div className="p-3 bg-gray-50 border border-gray-200 text-sm">
                <p className="font-medium">{duplicateSource.name}</p>
                <p className="text-gray-500 mt-1">
                  {duplicateSource.monthly_rent ? `${currency}${duplicateSource.monthly_rent}/${language === 'tr' ? 'ay' : 'mo'}` : ''}
                  {duplicateSource.floor ? ` | ${t('floor')}: ${duplicateSource.floor}` : ''}
                  {duplicateSource.size_sqm ? ` | ${duplicateSource.size_sqm} m²` : ''}
                </p>
                <p className="text-gray-500 mt-1">
                  {duplicateSource.electricity_type === 'prepaid_card_meter' ? t('prepaidMeter') : t('regularMeter')}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">{t('duplicateCount')}</label>
              <Input
                type="number"
                min="1"
                max="50"
                value={duplicateCount}
                onChange={(e) => setDuplicateCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                className="rounded-none mt-1"
                data-testid="duplicate-count-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => setIsDuplicateModalOpen(false)} className="rounded-none">
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleDuplicate} 
              disabled={duplicateLoading}
              className="bg-black text-white rounded-none hover:bg-gray-800"
              data-testid="duplicate-submit-btn"
            >
              {duplicateLoading ? t('loading') : `${t('duplicateUnit')} (${duplicateCount})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitsPage;
